'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth';
import useActiveTour from '@/state/tour';
import { dismissTour } from '@/services/tour';
import { TOUR_REGISTRY } from './tour-registry';
import { TourOverlay } from './tour-overlay';
import { TourTooltip } from './tour-tooltip';

export const TourController = () => {
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();
  const queryClient = useQueryClient();

  const { data } = useActiveTour(isAuthenticated);

  const version = data?.tour?.show ? data.tour.version : undefined;

  const definition = useMemo(
    () => (version ? TOUR_REGISTRY[version] : undefined),
    [version]
  );

  const isRouteMatch = definition?.route === pathname;
  const shouldActivate = !!definition && isRouteMatch;

  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const { mutate: dismiss } = useMutation({
    mutationFn: () => dismissTour(version!),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['active-tour'] });
    },
  });

  const handleDismiss = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    dismiss();
  }, [dismiss]);

  const handleNext = useCallback(() => {
    if (!definition) return;
    if (currentStep < definition.steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleDismiss();
    }
  }, [currentStep, definition, handleDismiss]);

  const handleRectChange = useCallback(
    (rect: { top: number; left: number; width: number; height: number }) => {
      setSpotlightRect(rect);
    },
    []
  );

  useEffect(() => {
    if (!shouldActivate || !definition) return;

    let attempts = 0;
    const maxAttempts = 20; // 2 seconds

    const interval = setInterval(() => {
      const el = document.getElementById(definition.targetId);
      if (el) {
        clearInterval(interval);
        setIsActive(true);
      } else if (++attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [shouldActivate, definition]);

  useEffect(() => {
    setIsActive(false);
    setCurrentStep(0);
    setSpotlightRect(null);
  }, [version, pathname]);

  useEffect(() => {
    if (!isActive) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleDismiss]);

  if (!isActive || !definition) return null;

  const step = definition.steps[currentStep];

  return (
    <>
      <TourOverlay
        targetId={definition.targetId}
        padding={definition.spotlightPadding ?? 8}
        onRectChange={handleRectChange}
        onDismiss={handleDismiss}
      />
      {definition.spotlightClickable && spotlightRect && (
        <div
          className="fixed z-modal cursor-pointer"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
          onMouseEnter={() => {
            const card = document.getElementById(definition.targetId);
            if (card) card.setAttribute('data-hover', 'true');
          }}
          onMouseLeave={() => {
            const card = document.getElementById(definition.targetId);
            if (card) card.removeAttribute('data-hover');
          }}
          onClick={() => {
            handleDismiss();
            const card = document.getElementById(definition.targetId);
            if (card) card.click();
          }}
          aria-hidden="true"
        />
      )}
      {spotlightRect && (
        <TourTooltip
          step={step}
          stepIndex={currentStep}
          totalSteps={definition.steps.length}
          spotlightRect={spotlightRect}
          onNext={handleNext}
          onSkip={handleDismiss}
        />
      )}
    </>
  );
};
